import { CertificationStatusEnumValue, getStatusConfig } from 'enums/CertificationStatusEnum';
import { translateLabel } from 'services/Utilities/labelUtils';
import { getDaysUntilQuarterEnd, isSubmissionCompletedBucket } from 'services/Utilities/submissionUtils';

const toDate = value => {
  if (!value) {
    return null;
  }

  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const normalizeSubmissions = submissions => (Array.isArray(submissions) ? submissions.filter(Boolean) : []);

const getNextSubmissionQuarterStartDate = submission =>
  toDate(submission?.nextSubmissionQuarter?.startDate ?? submission?.nextSubmissionQuarter?.StartDate ?? null);

export const getSubmissionActivityDate = submission =>
  toDate(
    submission?.dateLatestStatusChange ??
      submission?.dateUpdated ??
      submission?.dateIssued ??
      submission?.DateLatestStatusChange ??
      null,
  );

export const getLatestSubmission = submissions =>
  normalizeSubmissions(submissions)
    .slice()
    .sort((left, right) => {
      const leftTime = getSubmissionActivityDate(left)?.getTime() ?? 0;
      const rightTime = getSubmissionActivityDate(right)?.getTime() ?? 0;
      return rightTime - leftTime;
    })[0] ?? null;

export const getCompletedSubmissionCount = (submissions, referenceDate = new Date()) =>
  normalizeSubmissions(submissions).filter(submission => isSubmissionCompletedBucket(submission, referenceDate)).length;

export const getOngoingSubmissionCount = (submissions, referenceDate = new Date()) =>
  normalizeSubmissions(submissions).filter(submission => !isSubmissionCompletedBucket(submission, referenceDate)).length;

export const getCertifiedUntilDate = (submissions, referenceDate = new Date()) => {
  const normalizedReferenceDate = toDate(referenceDate) ?? new Date();

  const latestQualifiedSubmission =
    normalizeSubmissions(submissions)
      .filter(submission => {
        const nextQuarterStartDate = getNextSubmissionQuarterStartDate(submission);
        return nextQuarterStartDate && nextQuarterStartDate > normalizedReferenceDate;
      })
      .sort((left, right) => {
        const leftTime = getNextSubmissionQuarterStartDate(left)?.getTime() ?? 0;
        const rightTime = getNextSubmissionQuarterStartDate(right)?.getTime() ?? 0;
        return rightTime - leftTime;
      })[0] ?? null;

  const nextQuarterStartDate = getNextSubmissionQuarterStartDate(latestQualifiedSubmission);
  if (!nextQuarterStartDate) {
    return null;
  }

  const certifiedUntilDate = new Date(nextQuarterStartDate);
  certifiedUntilDate.setDate(certifiedUntilDate.getDate() - 1);
  return certifiedUntilDate;
};

export const formatCertificationLongDate = (value, language) => {
  const date = toDate(value);
  if (!date) {
    return null;
  }

  return date.toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export const getApprovedCriteriaOptionName = (submission, language, criteriaOptions = null) => {
  if (!submission) {
    return null;
  }

  if (submission.approvedCriteriaOption?.name) {
    return translateLabel(submission.approvedCriteriaOption.name, language);
  }

  if (!submission.approvedCriteriaOption_Id || !Array.isArray(criteriaOptions)) {
    return null;
  }

  const match = criteriaOptions.find(option => option.id === submission.approvedCriteriaOption_Id);
  return match?.name ? translateLabel(match.name, language) : null;
};

export const getExteriorStatusLabel = (submission, language, t) => {
  if (!submission) {
    return t('home.exteriorNeedsSubmission');
  }

  if (
    submission.status === CertificationStatusEnumValue.Passed ||
    submission.status === CertificationStatusEnumValue.PassedForAnotherOption
  ) {
    return t('home.exteriorApprovedUntilFurtherNotice');
  }

  if (submission.status === CertificationStatusEnumValue.Failed) {
    return t('home.exteriorDenied');
  }

  const statusConfig = getStatusConfig(submission.status);
  return language === 'fr' ? statusConfig?.labelFrench : statusConfig?.labelEnglish;
};

export const getExteriorStatusChipConfig = submission => {
  if (!submission) {
    return null;
  }

  const statusConfig = getStatusConfig(submission.status);

  if (
    submission.status === CertificationStatusEnumValue.Passed ||
    submission.status === CertificationStatusEnumValue.PassedForAnotherOption
  ) {
    return {
      color: '#2e7d32',
      bgColor: 'rgba(46, 125, 50, 0.1)',
      icon: statusConfig?.icon,
    };
  }

  if (submission.status === CertificationStatusEnumValue.Failed) {
    return {
      color: '#d32f2f',
      bgColor: 'rgba(211, 47, 47, 0.1)',
      icon: statusConfig?.icon,
    };
  }

  return {
    color: statusConfig?.color || '#999',
    bgColor: statusConfig?.bgColor || 'rgba(153,153,153,0.1)',
    icon: statusConfig?.icon,
  };
};

export const getSubmissionSentOnLabel = (submission, language, t) => {
  const formattedDate = formatCertificationLongDate(getSubmissionActivityDate(submission), language);
  return formattedDate ? t('home.submissionSentOn', { date: formattedDate }) : null;
};

export const getQuarterProgressSummary = (quarter, language, t, referenceDate = new Date()) => {
  if (!quarter) {
    return null;
  }

  const submissionLikeQuarter = { quarter };
  const daysUntilQuarterEnd = getDaysUntilQuarterEnd(submissionLikeQuarter, referenceDate);

  if (daysUntilQuarterEnd === null) {
    return quarter.internalName || null;
  }

  let daysLeftLabel = null;

  if (daysUntilQuarterEnd > 0) {
    daysLeftLabel = t('submissions.quarterDaysLeft', { count: daysUntilQuarterEnd });
  } else if (daysUntilQuarterEnd === 0) {
    daysLeftLabel = t('submissions.quarterEndsToday');
  } else {
    daysLeftLabel = t('submissions.quarterEndedOn', {
      date: formatCertificationLongDate(quarter.endDate, language),
    });
  }

  const quarterName = quarter.internalName || null;

  if (!quarterName) {
    return daysLeftLabel;
  }

  return t('dsmDashboard.certificationReport.notStartedQuarterProgress', {
    quarter: quarterName,
    progress: daysLeftLabel,
  });
};
